// components/playground/sctions/PLLineItemDetailsModal.jsx

import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../../utils'

const PLLineItemDetailsModal = ({ 
  accountInfo,
  monthlyTotals,
  transactions,
  metadata,
  isOpen, 
  onClose 
}) => {
  const [selectedMonth, setSelectedMonth] = useState(null);

  if (!isOpen || !accountInfo) return null;

  const CHART_HEIGHT = 160;

  // Get transactions for selected month (sorted by amount high to low)
  const getMonthTransactions = (monthNum, year) => {
    return transactions
      .filter(txn => txn.month_num === monthNum && txn.year === year)
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  };

  const selectedMonthTransactions = selectedMonth 
    ? getMonthTransactions(selectedMonth.month_num, selectedMonth.year)
    : [];

  // Chart calculations
  const chartData = monthlyTotals.map(month => ({
    ...month,
    absAmount: Math.abs(month.total_amount),
  }));

  console.log("chartData=",chartData)

  const maxAbsValue = Math.max(...chartData.map(d => d.absAmount), 0);
  
  // Calculate nice tick marks
  const calculateTickMarks = (max) => {
    let tickInterval;
    
    if (max <= 10) {
      tickInterval = 2;
    } else if (max <= 50) {
      tickInterval = 10;
    } else if (max <= 100) {
      tickInterval = 20;
    } else if (max <= 500) {
      tickInterval = 100;
    } else if (max <= 1000) {
      tickInterval = 200;
    } else if (max <= 5000) {
      tickInterval = 1000;
    } else if (max <= 10000) {
      tickInterval = 2000;
    } else if (max <= 50000) {
      tickInterval = 10000;
    } else if (max <= 100000) {
      tickInterval = 20000;
    } else if (max <= 500000) {
      tickInterval = 50000;
    } else {
      tickInterval = 100000;
    }
    
    const maxTick = Math.ceil(max / tickInterval) * tickInterval;
    
    const ticks = [];
    for (let i = 0; i <= maxTick; i += tickInterval) {
      ticks.push(i);
    }
    
    return { ticks, maxTick };
  };
  
  const { ticks, maxTick: rawMaxTick } = calculateTickMarks(maxAbsValue);
  const maxTick = rawMaxTick === 0 ? 1 : rawMaxTick;

  const chartHeight = 370;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Determine if this is an expense or income account
  const isExpenseAccount = accountInfo.classification === 'Expense';
  const barColor = isExpenseAccount ? 'bg-pink-500' : 'bg-teal-500';
  const barHoverColor = isExpenseAccount ? 'hover:bg-pink-600' : 'hover:bg-teal-600';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" 
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {accountInfo.account_name}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {accountInfo.classification} • {accountInfo.account_type}
              {metadata.data_updated_at && (
                <span className="ml-3 text-gray-500">
                  As of {new Date(metadata.data_updated_at).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Bar Chart */}
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                Monthly Breakdown - Click a bar to see transactions
              </h3>
              
              <div className="relative" style={{ height: CHART_HEIGHT + 60 }}> {/* Added extra space for labels */}
                <div className="flex gap-4 h-full">
                  {/* Y-axis labels */}
                  <div
                    className="flex flex-col justify-between"
                    style={{ width: '80px', height: CHART_HEIGHT }}
                  >
                    {ticks.slice().reverse().map((tick, index) => (
                      <div key={index} className="text-xs text-right text-gray-600">
                        {formatCurrency(tick)}
                      </div>
                    ))}
                  </div>
                  
                  {/* Chart area */}
                  <div className="flex-1 relative" style={{ height: CHART_HEIGHT + 40 }}>
                    {/* Y-axis line */}
                    <div className="absolute left-0 top-0 border-l-2 border-gray-400 z-10" style={{ height: CHART_HEIGHT }} />
                    
                    {/* Y-axis tick marks */}
                    {ticks.map((tick, index) => {
                      const tickPosition = (tick / maxTick) * CHART_HEIGHT;
                      return (
                        <div
                          key={`tick-${index}`}
                          className="absolute left-0 w-2 border-t border-gray-400"
                          style={{ bottom: `${40 + tickPosition}px` }} 
                        />
                      );
                    })}

                    {/* Bars container */}
                    <div 
                      className="absolute left-0 right-0 flex items-end justify-between gap-1"
                      style={{ 
                        height: CHART_HEIGHT,
                        bottom: '40px' // Position above the month labels
                      }}
                    >
                      {chartData.map((data, index) => {
                        const barHeightPx = Math.max((data.absAmount / maxTick) * CHART_HEIGHT, 3);
                        const isSelected = selectedMonth?.month_num === data.month_num && 
                                          selectedMonth?.year === data.year;
                        
                        return (
                          <div
                            key={index}
                            className="flex-1 flex flex-col justify-end items-center cursor-pointer group relative"
                            onClick={() => setSelectedMonth(data)}
                            style={{ 
                              minWidth: '40px',
                              height: '100%' // Take full height of container
                            }}
                          >
                            {/* Tooltip */}
                            <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 bg-gray-900 text-black text-xs rounded py-2 px-3 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30 shadow-lg">
                              <div className="font-semibold mb-1">{data.period_label}</div>
                              <div className="mb-1">{formatCurrency(data.total_amount)}</div>
                              <div className="text-gray-300 text-xs">
                                {data.transaction_count} transaction{data.transaction_count !== 1 ? 's' : ''}
                              </div>
                              <div className="text-blue-400 text-xs mt-1">
                                Click to see details
                              </div>
                            </div>
                            
                            {/* Bar */}
                            <div
                              className={`w-full rounded-t transition-all ${barColor} ${barHoverColor}
                                ${isSelected ? 'ring-2 ring-blue-500' : ''}
                              `}
                              style={{ 
                                height: `${barHeightPx}px`,
                                minHeight: '3px'
                              }}
                            >
                              {barHeightPx > 30 && (
                                <div className="flex items-center justify-center h-full px-1">
                                  <span className="text-white text-[9px] font-semibold">
                                    {formatCurrency(data.total_amount)}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Month label - now positioned relative to its parent */}
                            <div 
                              className="absolute text-xs text-gray-600 text-center font-medium whitespace-nowrap" 
                              style={{ 
                                bottom: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)'
                              }}
                            >
                              {data.period_label.split(' ')[0]}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    </div>
                </div>
              </div>
            </div>

            {/* Transaction Details Table */}
            {selectedMonth && (
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {selectedMonth.period_label} Transactions ({selectedMonthTransactions.length})
                    <span className="ml-3 text-gray-600 font-normal">
                      Total: {formatCurrency(selectedMonth.total_amount)}
                    </span>
                  </h3>
                </div>
                
                <div className="overflow-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-700">Date</th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-700">Entity</th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-700">Memo</th>
                        <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-700">Amount</th>
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-100">
                      {selectedMonthTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-3 py-4 text-center text-xs text-gray-500">
                            No transactions found for this month
                          </td>
                        </tr>
                      ) : (
                        selectedMonthTransactions.map((txn, idx) => (
                          <tr
                            key={`${txn.txn_id}-${idx}`}
                            className="hover:bg-gray-50"
                          >
                            {/* Date */}
                            <td className="px-3 py-1.5 text-xs text-gray-700 whitespace-nowrap">
                              {formatDate(txn.txn_date)}
                            </td>

                            {/* Entity – single line */}
                            <td className="px-3 py-1.5 text-xs text-gray-700 whitespace-nowrap truncate max-w-[200px]">
                              {txn.entity_name || '—'}
                              {/* {txn.entity_type && (
                                <span className="ml-1 text-gray-400">
                                  ({txn.entity_type})
                                </span> */}
                              {/* )} */}
                            </td>

                            {/* Memo */}
                            <td className="px-3 py-1.5 text-xs text-gray-600 truncate max-w-xs">
                              {txn.memo || '—'}
                            </td>

                            {/* Amount */}
                            <td className="px-3 py-1.5 text-xs text-right font-semibold text-gray-900 whitespace-nowrap">
                              {formatCurrency(txn.amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                </div>
              </div>
            )}

            {!selectedMonth && (
              <div className="text-center py-12 text-gray-500">
                <p className="text-sm">Click on a month's bar to see transaction details</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end flex-shrink-0">
          <button 
            onClick={onClose} 
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PLLineItemDetailsModal;